import type { Permission } from "@sitescene/constant";

import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { userHasPermission } from "@sitescene/constant";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

export { PERMISSIONS } from "@sitescene/constant";

/**
 * Hook that fetches the current user's permissions from Kinde.
 *
 * The query runs with `staleTime: 0` and `gcTime: 0`, so permissions are
 * refetched rather than shared across components — a deliberate trade so a
 * permission change takes effect without a reload. Returns `null` while
 * loading so callers can differentiate "no permissions" from "unknown yet".
 *
 * ---
 *
 * Use `checkPermission` to derive both states at once. Both values are
 * `false` while loading, so UI remains stable until the answer is known.
 *
 * `allowed` — permissions resolved AND user holds the permission.
 *   Use when you need a positive guard that also covers loading:
 *   - `isDisabled={!allowed}` — keeps controls disabled during load
 *   - Gating a mutation or side effect: `if (allowed) submit()`
 *   - Layout that should default to the no-access state: `allowed ? narrowCol : wideCol`
 *
 * `denied` — permissions resolved AND user does NOT hold the permission.
 *   Use when you only want to show something once denial is confirmed,
 *   to avoid flashing it briefly for users who do have access:
 *   - Explanatory UI: `{denied && <HelpCircle tooltip="Contact support..." />}`
 *   - "Get Support" hints: `{denied ? <SupportButton /> : <description />}`
 *   - Hiding action buttons: `{!denied && <Button>Edit</Button>}`
 *
 * @example
 * const { checkPermission } = usePermissions();
 * const { allowed: canEdit, denied: isEditDenied } = checkPermission(PERMISSIONS.EditZoneDetails);
 *
 * <Button isDisabled={!canEdit}>Manage</Button>
 * {isEditDenied && <HelpCircle tooltip="Contact support to enable this." />}
 */
export const usePermissions = () => {
	const {
		getPermissions,
		isAuthenticated,
		isLoading: isAuthLoading,
		user,
	} = useKindeAuth();

	const {
		data: permissions,
		error,
		isLoading,
		refetch,
	} = useQuery({
		enabled: isAuthenticated && !isAuthLoading,
		gcTime: 0,
		queryFn: async () => {
			const fetchedPermissions = await getPermissions();
			return (fetchedPermissions.permissions ?? []) as Permission[];
		},
		queryKey: ["kinde-permissions", user?.id],
		staleTime: 0,
	});

	const resolvedPermissions = permissions ?? null;

	const checkPermission = useCallback(
		(permission: Permission) => ({
			allowed:
				resolvedPermissions !== null &&
				userHasPermission(resolvedPermissions, permission),
			denied:
				resolvedPermissions !== null &&
				!userHasPermission(resolvedPermissions, permission),
		}),
		[resolvedPermissions],
	);

	return {
		checkPermission,
		error,
		isLoading: isLoading || isAuthLoading,
		permissions: resolvedPermissions,
		refetch,
	};
};
